import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscription, selectViewLimit, selectViewsRemaining } from "../../Redux/subscriptionSlice";
import InfluencerCard from "../components/InfluencerCard";
import Filters from "../components/Filters";
import Navbar from "../../components/Navbar";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import Footer from "../../components/Footer";
import { getInfluencers, buildListParams } from "../services/marketplaceApi";
import { useBrandAuthCheck } from "../../hooks/useBrandAuthCheck";
import RegisterModal from "../../components/RegisterModal";
import { clearUserState } from "../../Redux/userSlice";
import { useNavigate } from "react-router-dom";

const PER_PAGE = 20;

function filtersToApiParams(filters, page) {
  const locationStr =
    filters.location && typeof filters.location === "object"
      ? [filters.location.country, filters.location.region, filters.location.city]
        .filter(Boolean)
        .join(", ")
      : "";

  const contentTypeStr =
    Array.isArray(filters.contentType) && filters.contentType.length > 0
      ? filters.contentType[0]
      : "";

  return buildListParams({
    page,
    limit: PER_PAGE,
    isActive: true,
    search: filters.search || "",
    platform: filters.platform || "",
    category: filters.category || "",
    contentType: contentTypeStr,
    followersMin: filters.followers?.min,
    followersMax: filters.followers?.max,
    priceMax:
      filters.price != null && filters.price !== ""
        ? Number(filters.price)
        : undefined,
    gender: filters.gender || "",
    language: filters.language || "",
    location: locationStr,
    isVerified: filters.verified === true ? true : undefined,
  });
}

function sortInfluencersVerifiedFirst(raw) {
  const withIndex = raw.map((influencer, idx) => ({ influencer, idx }));
  withIndex.sort((a, b) => {
    const aVerified = Boolean(a.influencer?.isVerified);
    const bVerified = Boolean(b.influencer?.isVerified);
    if (aVerified !== bVerified) return aVerified ? -1 : 1;
    return a.idx - b.idx;
  });
  return withIndex.map((x) => x.influencer);
}

const MarketPlace = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isBrand, isAuthenticated } = useBrandAuthCheck();
  const [showJoinModal, setShowJoinModal] = useState(false);

  const { data: subscription, loading: subLoading } = useSelector(
    (state) => state.subscription
  );
  const viewsRemaining = useSelector(selectViewsRemaining);
  const viewLimit = useSelector(selectViewLimit);

  const planType = useMemo(() => subscription?.planType || "basic", [subscription?.planType]);

  const [filters, setFilters] = useState({
    search: "",
    platform: "",
    category: "",
    contentType: [],
    followers: { min: 0, max: 100000000 },
    location: {},
    gender: "",
    price: null,
    language: null,
    age: null,
    ethnicity: null,
    verified: null,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const listParams = useMemo(
    () => filtersToApiParams(filters, currentPage),
    [filters, currentPage]
  );

  const {
    data: listData,
    isPending: listPending,
    isError: listError,
    isPlaceholderData: listIsPlaceholder,
  } = useQuery({
    queryKey: ["marketplace", "influencers", listParams],
    queryFn: async () => {
      const result = await getInfluencers(listParams);
      const raw = result.data?.influencers ?? [];
      const influencers = sortInfluencersVerifiedFirst(raw);
      const pagination = result.data?.pagination ?? { total: 0, page: 1, pages: 1 };
      return { influencers, pagination };
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const influencers = listData?.influencers ?? [];
  const pagination = listData?.pagination ?? { total: 0, page: 1, pages: 1 };

  /**
   * Fetch subscription once if authenticated brand
   */
  useEffect(() => {
    if (isAuthenticated && isBrand && !subscription) {
      dispatch(fetchSubscription());
    }
  }, [dispatch, isAuthenticated, isBrand, subscription]);

  const showListSpinner =
    subLoading || (listPending && !listIsPlaceholder && !listData);

  const setFiltersAndResetPage = useCallback((next) => {
    setFilters(next);
    setCurrentPage(1);
  }, []);

  const totalPages = pagination.pages || 1;

  const goNext = useCallback(() => {
    if (!isAuthenticated || !isBrand) {
      setShowJoinModal(true);
      return;
    }
    setCurrentPage((p) => Math.min(p + 1, totalPages));
  }, [totalPages, isAuthenticated, isBrand]);

  const goBack = useCallback(() => {
    setCurrentPage((p) => Math.max(p - 1, 1));
  }, []);

  const showPagination = pagination.total > PER_PAGE;

  /**
   * Blur logic
   */
  const shouldBlurAll = isAuthenticated && isBrand && subscription && planType === "basic";

  const showRemainingViews =
    isAuthenticated &&
    isBrand &&
    subscription &&
    planType !== "basic" &&
    typeof viewsRemaining === "number";

  const handleRegisterAs = useCallback(
    (type) => {
      setShowJoinModal(false);
      if (type === "influencer") {
        navigate("/influencer/login");
        return;
      }

      const userType = localStorage.getItem("userType");
      if (userType === "influancer") {
        dispatch(clearUserState());
      }
      navigate("/company/login");
    },
    [dispatch, navigate]
  );

  return (
    <>
      <Navbar />

      <div className="px-2 md:px-4 py-2 sm:py-6 max-w-7xl mx-auto min-h-screen relative">
        {/* Views left: centered pill at top on mobile/tablet; on desktop it lives in the search row (inside Filters) */}
        {showRemainingViews && (
          <div className="lg:hidden flex justify-end mb-4 sm:mb-5">
            <div className="flex items-center gap-1.5 px-4 sm:px-5 sm:py-1 py-2 rounded-full bg-gray-100 border border-gray-200 text-sm font-medium text-gray-700">
              <Eye size={16} className="shrink-0 text-gray-600" />
              <span>
                {viewsRemaining} / {viewLimit} views left today
              </span>
            </div>
          </div>
        )}

        <Filters
          filters={filters}
          setFilters={setFiltersAndResetPage}
          planType={planType}
          showRemainingViews={showRemainingViews}
          viewsRemaining={viewsRemaining}
          viewLimit={viewLimit}
        />

        <h2 className="text-lg sm:text-xl font-bold mt-6 sm:mt-8">
          Influencers Marketplace
        </h2>

        {showListSpinner ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-800" />
          </div>
        ) : listError ? (
          <p className="mt-8 text-red-600 text-center">Failed to load influencers</p>
        ) : influencers.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-6 sm:mt-8">
              {influencers.map((influencer) => (
                <InfluencerCard
                  key={influencer.id}
                  influencer={influencer}
                  blur={shouldBlurAll}
                />
              ))}
            </div>

            {showPagination && (
              <div className="flex justify-center items-center gap-3 mt-10 mb-6">
                {currentPage > 1 && (
                  <button
                    onClick={goBack}
                    className="inline-flex items-center gap-1.5 md:px-5 px-2 md:py-2.5 py-1 rounded-lg border-2 border-gray-800 font-semibold bg-white hover:bg-gray-50 transition"
                  >
                    <ChevronLeft size={18} />
                    Back
                  </button>
                )}

                <span className="text-sm font-medium text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>

                {currentPage < totalPages && (
                  <button
                    onClick={goNext}
                    className="inline-flex items-center gap-1.5 md:px-5 px-2 md:py-2.5 py-1 rounded-lg border-2 border-gray-800 font-semibold bg-white hover:bg-gray-50 transition"
                  >
                    Next Page
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="mt-8 text-gray-500 text-center">
            No influencers match your filters.
          </p>
        )}
      </div>

      <Footer />

      <RegisterModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onRegisterAs={handleRegisterAs}
      />
    </>
  );
};

export default MarketPlace;
